import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { coursesData } from './courses/coursesData';
import BrochureModal from '../components/BrochureModal';
import SEO from '../components/SEO';
import '../global.css'; // Ensure we have styles

// Helper to get color class based on category (matching global.css border logic)
const getCategoryColor = (category) => {
    const map = {
        'Software': '#3b82f6', // Blue
        'Design': '#a855f7', // Purple
        'Business Courses': '#22c55e', // Green
        'Medical': '#ef4444', // Red
        'Competetive Exams': '#f59e0b', // Amber
        'Languages': '#ec4899', // Pink
        'Agriculture': '#84cc16', // Lime
        'Mechanical': '#6366f1', // Indigo
        'Civil': '#64748b', // Slate
        'Lifestyle': '#14b8a6', // Teal
    };
    return map[category] || '#ea580c'; // Default Orange
};

const CourseDetail = ({ seoSlug }) => {

    const { slugOrId, id } = useParams();
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);

    // Resolve course by seoSlug prop, slugOrId param, or legacy id param
    const paramKey = seoSlug || slugOrId || id;

    const course = coursesData.find(c => {
        if (!paramKey) return false;
        if (c.seoPath && c.seoPath.toLowerCase() === paramKey.toLowerCase()) return true;
        if (c.slug && c.slug.toLowerCase() === paramKey.toLowerCase()) return true;
        if (c.id.toString() === paramKey.toString()) return true;
        return false;
    });

    useEffect(() => {
        window.scrollTo(0, 0);

        // Dynamically load Calendly script
        const script = document.createElement('script');
        script.src = "https://assets.calendly.com/assets/external/widget.js";
        script.async = true;
        document.body.appendChild(script);

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, [slugOrId, id, seoSlug]);

    if (!course) {
        return (
            <div className="gk-container" style={{ padding: '100px 20px', textAlign: 'center' }}>
                <h2>Course Not Found</h2>
                <p style={{ color: '#64748b', margin: '15px 0' }}>The requested course could not be located. Explore our full catalog of certification courses.</p>
                <Link to="/courses" style={{ maxWidth: '220px', margin: '20px auto', display: 'inline-block', padding: '14px 24px', background: '#ea580c', color: '#fff', borderRadius: '12px', textDecoration: 'none', fontWeight: 'bold' }}>Browse All Courses</Link>
            </div>
        );
    }

    const themeColor = getCategoryColor(course.category);

    const handleBack = () => {
        navigate('/courses', { state: { category: course.category, title: course.title } });
    };

    const handleDownloadBrochure = () => {
        setShowModal(true);
    };

    const handleModalDownload = () => {
        const link = document.createElement('a');
        link.href = course.brochureUrl || '/MERN STACK.pdf';
        link.download = `${course.title.replace(/\s+/g, '-')}-Curriculum.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Determine canonical path (SEO friendly slug)
    const canonicalPath = course.seoPath 
        ? `/${course.seoPath}` 
        : (course.slug ? `/course/${course.slug}` : `/course/${course.id}`);

    const pageTitle = course.metaTitle || `${course.title} Course Online with Certificate | Skill Ascent`;
    const pageDescription = course.metaDescription || course.description || `Learn ${course.title} at Skill Ascent. Comprehensive online certification training with practical hands-on projects, industry mentorship, and recognized certificates.`;
    const pageKeywords = course.keywords || `${course.title}, ${course.category}, ${course.role}, online course with certificate, skill ascent training`;

    const breadcrumbs = [
        { name: "Home", url: "/" },
        { name: "Courses", url: "/courses" },
        { name: course.title, url: canonicalPath }
    ];

    const courseSchema = {
        "@context": "https://schema.org",
        "@type": "Course",
        "name": course.title,
        "description": pageDescription,
        "courseCode": `SA-${course.id}`,
        "provider": {
            "@type": "EducationalOrganization",
            "name": "Skill Ascent",
            "sameAs": "https://www.skillascent.in/",
            "url": "https://www.skillascent.in/"
        },
        "url": `https://www.skillascent.in${canonicalPath}`,
        "offers": {
            "@type": "Offer",
            "category": course.category,
            "availability": "https://schema.org/InStock",
            "priceCurrency": "INR",
            "url": `https://www.skillascent.in${canonicalPath}`
        },
        "timeRequired": course.duration,
        "educationalCredentialAwarded": "Skill Ascent Industry Recognized Certificate",
        "occupationalCategory": course.role,
        "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "online",
            "courseWorkload": course.duration
        }
    };

    // Inline styles for dynamic theming
    const heroStyle = {
        background: `linear-gradient(135deg, ${themeColor}20 0%, #ffffff 100%)`,
        padding: '50px 20px',
        paddingTop: '120px',
        position: 'relative',
        overflow: 'hidden'
    };

    const sectionTitleStyle = {
        fontSize: '2rem',
        fontWeight: '800',
        color: '#0f172a',
        marginBottom: '40px',
        position: 'relative',
        display: 'inline-block'
    };

    return (

        <div className="course-detail-page">
            <SEO
                title={pageTitle}
                description={pageDescription}
                keywords={pageKeywords}
                canonicalPath={canonicalPath}
                schemaData={courseSchema}
                breadcrumbs={breadcrumbs}
            />

            {/* Custom Responsive Styles */}
            <style>
                {`
                    .course-detail-page, .course-detail-page * {
                        font-family: 'Barlow', system-ui, sans-serif !important;
                    }
                    .cd-explore-btn {
                        padding: 14px 24px;
                        border-radius: 12px;
                        font-weight: 700;
                        font-size: 1rem;
                        cursor: pointer;
                        transition: all 0.3s ease;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 10px;
                        text-decoration: none;
                        width: 100%;
                        margin-bottom: 15px;
                    }
                    .cd-explore-btn:hover {
                        transform: translateY(-3px);
                        box-shadow: 0 10px 20px -5px rgba(0,0,0,0.15);
                    }
                    .cd-meta-item {
                        font-size: 1rem;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        padding: 8px 16px;
                        background: #ffffff;
                        border-radius: 50px;
                        border: 1px solid #e2e8f0;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                    }
                    @media (max-width: 768px) {
                        .cd-hero-card { padding: 25px !important; width: 100% !important; }
                        .cd-price-current { font-size: 2rem !important; }
                        .cd-course-grid { grid-template-columns: 1fr !important; }
                        .cd-calendly-card { padding: 0px !important; background: transparent !important; border: none !important; box-shadow: none !important; border-radius: 0 !important; }
                    }
                `}
            </style>

            {/* Hero Section */}
            <section style={heroStyle}>
                <div className="gk-container" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '40px' }}>
                    <div style={{ flex: '1 1 300px', minWidth: '280px', maxWidth: '600px' }}>

                        {/* Breadcrumb Navigation for SEO & UX */}
                        <nav aria-label="Breadcrumb" style={{ marginBottom: '18px' }}>
                            <ol style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', listStyle: 'none', padding: 0, margin: 0, fontSize: '0.85rem' }}>
                                <li>
                                    <Link to="/" style={{ color: '#475569', textDecoration: 'none', fontWeight: '600' }}>Home</Link>
                                </li>
                                <li style={{ color: '#94a3b8' }}>/</li>
                                <li>
                                    <Link to="/courses" style={{ color: '#475569', textDecoration: 'none', fontWeight: '600' }}>Courses</Link>
                                </li>
                                <li style={{ color: '#94a3b8' }}>/</li>
                                <li aria-current="page" style={{ color: themeColor, fontWeight: '700' }}>
                                    {course.title}
                                </li>
                            </ol>
                        </nav>

                        {/* Back Button */}
                        <button
                            onClick={handleBack}
                            style={{
                                display: 'flex',
                                width: 'fit-content',
                                alignItems: 'center',
                                gap: '8px',
                                background: '#ffffff',
                                border: '1px solid #e2e8f0',
                                color: '#475569',
                                fontSize: '0.95rem',
                                fontWeight: '600',
                                marginBottom: '25px',
                                cursor: 'pointer',
                                padding: '10px 20px',
                                borderRadius: '50px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateX(-5px)';
                                e.currentTarget.style.color = themeColor;
                                e.currentTarget.style.borderColor = themeColor;
                                e.currentTarget.style.boxShadow = `0 10px 15px -3px ${themeColor}20`;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateX(0)';
                                e.currentTarget.style.color = '#475569';
                                e.currentTarget.style.borderColor = '#e2e8f0';
                                e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"></path><polyline points="12 19 5 12 12 5"></polyline></svg>
                            Back to Courses
                        </button>


                        <span style={{
                            background: themeColor,
                            color: '#fff',
                            padding: '6px 12px',
                            borderRadius: '50px',
                            fontSize: '0.85rem',
                            fontWeight: '700',
                            marginBottom: '20px',
                            display: 'inline-block'
                        }}>
                            {course.category}
                        </span>
                        <h1 style={{ fontSize: 'clamp(2.2rem, 5vw + 1rem, 3.5rem)', fontWeight: '800', color: '#0f172a', lineHeight: '1.2', marginBottom: '20px' }}>
                            {course.title}
                        </h1>
                        <p style={{ fontSize: 'clamp(1rem, 2vw + 0.5rem, 1.2rem)', color: '#475569', lineHeight: '1.6', marginBottom: '30px', maxWidth: '600px' }}>
                            {course.description || "Master the skills needed to excel in this field with our comprehensive, industry-aligned curriculum."}
                        </p>

                        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                            <div className="cd-meta-item">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={themeColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                                <span style={{ color: '#0f172a', fontWeight: '600' }}>{course.duration}</span>
                            </div>
                            <div className="cd-meta-item">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={themeColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                                <span style={{ color: '#0f172a', fontWeight: '600' }}>{course.role}</span>
                            </div>
                        </div>
                    </div>

                    {/* Course Card / Image */}
                    <div className="cd-hero-card" style={{
                        flex: '1 1 300px',
                        maxWidth: '450px',
                        background: '#fff',
                        padding: '40px',
                        borderRadius: '24px',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
                        border: `1px solid ${themeColor}30`,
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            width: '80px',
                            height: '80px',
                            margin: '0 0 20px 0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `linear-gradient(135deg, ${themeColor}15 0%, ${themeColor}05 100%)`,
                            color: themeColor,
                            border: `1px solid ${themeColor}20`,
                            borderRadius: '50%',
                            boxShadow: `0 8px 20px -5px ${themeColor}30`
                        }}>
                            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                                <line x1="7" y1="7" x2="7.01" y2="7"></line>
                            </svg>
                        </div>

                        <div style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            justifyContent: 'center',
                            gap: '12px',
                            marginBottom: '30px',
                            width: '100%'
                        }}>
                            <span className="cd-price-current" style={{
                                fontSize: '2.5rem',
                                fontWeight: '800',
                                color: themeColor,
                                lineHeight: 1
                            }}>
                                {course.price}
                            </span>
                            {course.originalPrice && (
                                <span className="gk-price-original" style={{
                                    fontSize: '1.25rem',
                                    color: '#94a3b8',
                                    textDecoration: 'line-through',
                                    fontWeight: '500'
                                }}>
                                    {course.originalPrice}
                                </span>
                            )}
                        </div>

                        {(() => {
                            const calendlyUrls = {
                                "Video Editing": "https://calendly.com/digitalmarketing-skillascent/new-meeting?hide_event_type_details=1&hide_gdpr_banner=1&background_color=ffffff&primary_color=ff5600",
                                "Graphic Designing": "https://calendly.com/digitalmarketing-skillascent/graphic-designing-demo?hide_event_type_details=1&hide_gdpr_banner=1&background_color=ffffff&primary_color=ff4000",
                                "Digital Marketing": "https://calendly.com/digitalmarketing-skillascent/skill-ascent-digital-marketing?hide_event_type_details=1&hide_gdpr_banner=1&background_color=ffffff&primary_color=ff3800"
                            };
                            const specificCalendlyUrl = calendlyUrls[course.title];

                            if (specificCalendlyUrl) {
                                return (
                                    <>
                                        {/* <button
                                            className="cd-explore-btn"
                                            onClick={() => {
                                                const section = document.getElementById('demo-section');
                                                if (section) {
                                                    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                }
                                            }}
                                            style={{
                                                background: '#fff',
                                                color: themeColor,
                                                border: `2px solid ${themeColor}`,
                                            }}
                                        >
                                            Book Your Free Demo
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                                        </button> */}
                                        <Link to="/register" className="cd-explore-btn" style={{
                                            background: '#fff',
                                            color: themeColor,
                                            border: `2px solid ${themeColor}`,
                                        }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = themeColor;
                                                e.currentTarget.style.color = '#fff';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = '#fff';
                                                e.currentTarget.style.color = themeColor;
                                            }}
                                        >
                                            Enroll Now
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                                        </Link>
                                    </>
                                );
                            }

                            return (
                                <Link to="/register" className="cd-explore-btn" style={{
                                    background: themeColor,
                                    color: '#fff',
                                    border: `2px solid ${themeColor}`,
                                }}>
                                    Enroll Now
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                                </Link>
                            );
                        })()}

                        <button
                            className="cd-explore-btn"
                            onClick={handleDownloadBrochure}
                            style={{
                                background: themeColor,
                                color: '#fff',
                                border: `2px solid ${themeColor}`,
                            }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                            Download Curriculum
                        </button>
                    </div>
                </div>
            </section>

            {/* {(() => {
                const calendlyUrls = {
                    "Video Editing": "https://calendly.com/digitalmarketing-skillascent/new-meeting?hide_event_type_details=1&hide_gdpr_banner=1&background_color=ffffff&primary_color=ff5600",
                    "Graphic Designing": "https://calendly.com/digitalmarketing-skillascent/graphic-designing-demo?hide_event_type_details=1&hide_gdpr_banner=1&background_color=ffffff&primary_color=ff4000",
                    "Digital Marketing": "https://calendly.com/digitalmarketing-skillascent/skill-ascent-digital-marketing?hide_event_type_details=1&hide_gdpr_banner=1&background_color=ffffff&primary_color=ff3800"
                };
                const specificCalendlyUrl = calendlyUrls[course.title];

                if (specificCalendlyUrl) {
                    return (
                        <section id="demo-section" style={{ padding: '40px 20px', background: `${themeColor}08` }}>
                            <div className="gk-container">
                                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                    <h2 style={{ fontSize: 'clamp(2rem, 5vw + 1rem, 2.5rem)', fontWeight: '800', color: '#0f172a', marginBottom: '10px' }}>
                                        Book Your <span style={{ color: themeColor }}>Free Demo</span>
                                    </h2>
                                    <p style={{ fontSize: 'clamp(0.95rem, 2vw + 0.5rem, 1.1rem)', color: '#64748b' }}>
                                        Check our availability and schedule a live session with our experts.
                                    </p>
                                </div>
                                <div className="cd-calendly-card" style={{
                                    maxWidth: '650px',
                                    margin: '0 auto',
                                    background: '#ffffff',
                                    borderRadius: '24px',
                                    padding: '20px',
                                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.08)',
                                    border: `1px solid ${themeColor}30`,
                                    overflow: 'hidden'
                                }}>
                                    <div
                                        className="calendly-inline-widget"
                                        data-url={specificCalendlyUrl}
                                        style={{ width: '100%', minWidth: '100%', height: '650px' }}
                                    ></div>
                                </div>
                            </div>
                        </section>
                    );
                }
                return null;
            })()} */}

            <BrochureModal
                isOpen={showModal}
                courseTitle={course.title}
                onDownload={handleModalDownload}
                onClose={() => setShowModal(false)}
            />

            {/* Curriculum / Details Section */}
            <section style={{ padding: '60px 20px', background: '#fff' }}>
                <div className="gk-container">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '40px', flexWrap: 'wrap', gap: '20px' }}>
                        <h2 style={{ ...sectionTitleStyle, marginBottom: 0 }}>What You Will Learn</h2>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 20px',
                            background: '#f8fafc',
                            borderRadius: '50px',
                            border: '1px solid #e2e8f0',
                            fontSize: '0.9rem',
                            color: '#64748b'
                        }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                            For detailed curriculum, please click <span style={{ fontWeight: '700', color: themeColor, cursor: 'pointer' }} onClick={handleDownloadBrochure}>Download Curriculum</span>
                        </div>
                    </div>

                    <div className="cd-course-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: '24px'
                    }}>
                        {(course.curriculum || ["Fundamental Concepts", "Advanced Techniques", "Practical Projects", "Industry Tools", "Career Preparation"]).map((item, index) => (
                            <div key={index} style={{
                                padding: '20px',
                                background: '#ffffff',
                                borderRadius: '16px',
                                border: '1px solid #f1f5f9',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                transition: 'transform 0.2s ease',
                                cursor: 'default'
                            }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-3px)';
                                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.05)';
                                    e.currentTarget.style.borderColor = `${themeColor}40`;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.02)';
                                    e.currentTarget.style.borderColor = '#f1f5f9';
                                }}
                            >
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    background: `${themeColor}15`,
                                    color: themeColor,
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                </div>
                                <span style={{ fontSize: '1.05rem', fontWeight: '600', color: '#334155' }}>{item}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CourseDetail;
